/**
 * Epitome ECharts theme.
 * Values mirror src/tokens.css so charts share the same visual identity
 * as the rest of the kit's components.
 *
 * Usage:
 *   import * as echarts from 'echarts/core';
 *   import { epitomeTheme } from '@epitome-analytics/dashboard-kit/theme';
 *   echarts.registerTheme('epitome', epitomeTheme);
 *   const chart = echarts.init(el, 'epitome');
 */
export const tokens = {
    navy: '#0D2E4A',
    blue: '#0E5A96',
    teal: '#68BCA9',
    amber: '#F5A623',
    text: '#1A2733',
    muted: '#5A6A78',
    faint: '#8A9AA8',
    border: '#E2E8EE',
    surface: '#F7F9FB',
    white: '#FFFFFF',
};
export const chartPalette = [
    '#0E5A96', // blue
    '#68BCA9', // teal
    '#F5A623', // amber
    '#6B5BD2', // purple
    '#E27D60', // coral
    '#2F8F73', // deep teal
    '#7AA7CB', // light blue
    '#B8AC75', // khaki
];
const FONT_UI = "'Inter', -apple-system, BlinkMacSystemFont, sans-serif";
const FONT_DISPLAY = "'Barlow Condensed', -apple-system, sans-serif";
export const epitomeTheme = {
    color: chartPalette,
    backgroundColor: 'transparent',
    textStyle: {
        fontFamily: FONT_UI,
        color: tokens.text,
    },
    title: {
        textStyle: {
            fontFamily: FONT_DISPLAY,
            fontWeight: 700,
            fontSize: 16,
            color: tokens.navy,
        },
        subtextStyle: {
            fontFamily: FONT_UI,
            fontSize: 12,
            color: tokens.muted,
        },
        left: 0,
        top: 0,
    },
    grid: {
        left: 48,
        right: 24,
        top: 32,
        bottom: 32,
        containLabel: true,
    },
    legend: {
        textStyle: { color: tokens.muted, fontSize: 12 },
        icon: 'roundRect',
        itemWidth: 10,
        itemHeight: 10,
        itemGap: 16,
    },
    tooltip: {
        backgroundColor: tokens.white,
        borderColor: tokens.border,
        borderWidth: 1,
        padding: [10, 14],
        textStyle: { color: tokens.text, fontSize: 12, fontFamily: FONT_UI },
        extraCssText: 'box-shadow: 0 8px 24px rgba(13, 46, 74, 0.12); border-radius: 8px;',
        axisPointer: {
            lineStyle: { color: tokens.faint, type: 'dashed', width: 1 },
            crossStyle: { color: tokens.faint },
        },
    },
    categoryAxis: {
        axisLine: { lineStyle: { color: tokens.border } },
        axisTick: { show: false },
        axisLabel: { color: tokens.muted, fontSize: 11 },
        splitLine: { show: false },
    },
    valueAxis: {
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: tokens.muted, fontSize: 11 },
        splitLine: { lineStyle: { color: tokens.border, type: 'dashed' } },
    },
    timeAxis: {
        axisLine: { lineStyle: { color: tokens.border } },
        axisTick: { show: false },
        axisLabel: { color: tokens.muted, fontSize: 11 },
        splitLine: { show: false },
    },
    line: {
        lineStyle: { width: 2 },
        symbol: 'circle',
        symbolSize: 6,
        smooth: false,
    },
    bar: {
        itemStyle: { borderRadius: [3, 3, 0, 0] },
        barMaxWidth: 28,
    },
    scatter: {
        symbolSize: 8,
        itemStyle: { opacity: 0.85 },
    },
    visualMap: {
        inRange: { color: ['#DCEAF5', '#0E5A96', '#0D2E4A'] },
        textStyle: { color: tokens.muted, fontSize: 11 },
    },
    dataZoom: {
        backgroundColor: tokens.surface,
        fillerColor: 'rgba(14, 90, 150, 0.12)',
        borderColor: tokens.border,
        handleStyle: { color: tokens.blue },
        textStyle: { color: tokens.muted },
    },
};
/** Diverging palette for variance/delta charts (negative → positive). */
export const variancePalette = {
    negative: '#C5403F',
    neutral: tokens.faint,
    positive: tokens.teal,
};
/** Sequential palette for choropleth/heatmap. */
export const sequentialPalette = ['#DCEAF5', '#7AA7CB', '#0E5A96', '#0D2E4A'];
